import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Divider,
  Avatar,
  Typography,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon       from '@mui/icons-material/Logout';
import { jwtDecode }   from 'jwt-decode';

import { DIRECTOR_NAV_ITEMS, DIRECTOR_BOTTOM_NAV_ITEMS } from './nav-config-director';
import { useGetDirectorMeQuery } from '../../redux/services/director-api';
import { APP_BAR_COLOR } from '../../constants/env';

const DRAWER_WIDTH    = 240;
const COLLAPSED_WIDTH = 65;

const COLORS = {
  // Verde de marca en produccion, ambar en dev: el armazon entero cambia de color
  // para que no haya forma de confundir los dos entornos de un vistazo.
  bg:           APP_BAR_COLOR,
  bgHover:      'rgba(255,255,255,0.08)',
  bgActive:     'rgba(255,255,255,0.15)',
  borderActive: '#ffffff',
  text:         'rgba(255,255,255,0.85)',
  textActive:   '#ffffff',
  divider:      'rgba(255,255,255,0.12)',
  icon:         'rgba(255,255,255,0.70)',
  iconActive:   '#ffffff',
  label:        'rgba(255,255,255,0.38)',
};

function SectionLabel({ text, open }) {
  if (!open) return null;
  return (
    <Typography
      variant="overline"
      sx={{
        px: 2,
        pt: 1.5,
        pb: 0.25,
        display: 'block',
        fontSize: '0.65rem',
        letterSpacing: '0.08em',
        color: COLORS.label,
        lineHeight: 1.4,
      }}
    >
      {text}
    </Typography>
  );
}

function NavItem({ title, path, icon: Icon, open, active, onClick }) {
  return (
    <Tooltip title={open ? '' : title} placement="right" arrow>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          onClick={onClick}
          sx={{
            minHeight: 44,
            justifyContent: open ? 'initial' : 'center',
            px: 2,
            borderLeft: `3px solid ${active ? COLORS.borderActive : 'transparent'}`,
            backgroundColor: active ? COLORS.bgActive : 'transparent',
            borderRadius: '0 8px 8px 0',
            mr: 1,
            '&:hover': { backgroundColor: COLORS.bgHover },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: open ? 2 : 'auto',
              justifyContent: 'center',
              color: active ? COLORS.iconActive : COLORS.icon,
            }}
          >
            <Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={title}
            sx={{
              opacity: open ? 1 : 0,
              transition: 'opacity 0.2s',
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                fontWeight: active ? 600 : 400,
                color: active ? COLORS.textActive : COLORS.text,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    </Tooltip>
  );
}

function DirectorSidebar({ mobileOpen = false, onMobileClose = () => {} }) {
  const [open, setOpen] = useState(true);
  const navigate        = useNavigate();
  const location        = useLocation();
  const theme           = useTheme();
  const isMobile        = useMediaQuery(theme.breakpoints.down('md'));

  const token    = localStorage.getItem('director_token');
  let fullname   = 'Director';
  try {
    const decoded = jwtDecode(token);
    fullname = decoded.fullname || decoded.username || 'Director';
  } catch {}
  const avatarLetter = fullname[0]?.toUpperCase() ?? 'D';

  const { data: meData } = useGetDirectorMeQuery();
  const clubName = meData?.club?.name || '';

  const isActive = (path) => location.pathname.startsWith(path);

  const handleLogout = () => {
    localStorage.removeItem('director_token');
    navigate('/director/login');
  };

  const drawerWidth = isMobile ? DRAWER_WIDTH : (open ? DRAWER_WIDTH : COLLAPSED_WIDTH);

  const handleNavClick = (path) => {
    navigate(path);
    if (isMobile) onMobileClose();
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={isMobile ? onMobileClose : undefined}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? 0 : drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          overflowX: 'hidden',
          backgroundColor: COLORS.bg,
          borderRight: 'none',
          boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
          transition: (t) =>
            t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: open
                ? t.transitions.duration.enteringScreen
                : t.transitions.duration.leavingScreen,
            }),
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Cabecera / toggle ─────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: (isMobile || open) ? 'space-between' : 'center',
          minHeight: 64,
          px: (isMobile || open) ? 2 : 0,
          flexShrink: 0,
        }}
      >
        {(isMobile || open) && (
          <Typography
            variant="subtitle1"
            sx={{ color: COLORS.textActive, fontWeight: 700, letterSpacing: 0.5 }}
          >
            Portal Director
          </Typography>
        )}
        {!isMobile && (
          <IconButton onClick={() => setOpen((v) => !v)} sx={{ color: COLORS.text }} size="small">
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: COLORS.divider, flexShrink: 0 }} />

      {/* ── Sección "Mi club" ─────────────────────────────────────────── */}
      <List sx={{ pt: 0.5, flex: 1 }}>
        <SectionLabel text="Mi club" open={isMobile ? true : open} />
        {DIRECTOR_NAV_ITEMS.map(({ title, path, icon }) => (
          <NavItem
            key={path}
            title={title}
            path={path}
            icon={icon}
            open={isMobile ? true : open}
            active={isActive(path)}
            onClick={() => handleNavClick(path)}
          />
        ))}
      </List>

      {/* ── Sección "Cuenta" ──────────────────────────────────────────── */}
      <Divider sx={{ borderColor: COLORS.divider }} />
      <List sx={{ pb: 0.5 }}>
        <SectionLabel text="Cuenta" open={isMobile ? true : open} />
        {DIRECTOR_BOTTOM_NAV_ITEMS.map(({ title, path, icon }) => (
          <NavItem
            key={path}
            title={title}
            path={path}
            icon={icon}
            open={isMobile ? true : open}
            active={isActive(path)}
            onClick={() => handleNavClick(path)}
          />
        ))}
      </List>

      {/* ── Perfil + logout ───────────────────────────────────────────── */}
      <Divider sx={{ borderColor: COLORS.divider }} />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: (isMobile || open) ? 1.5 : 1,
          justifyContent: (isMobile || open) ? 'flex-start' : 'center',
          flexShrink: 0,
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'rgba(255,255,255,0.2)',
            fontSize: '0.8rem',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {avatarLetter}
        </Avatar>

        {(isMobile || open) && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: COLORS.text,
                fontSize: '0.8rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {fullname}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: COLORS.icon,
                fontSize: '0.7rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {clubName || 'Director'}
            </Typography>
          </Box>
        )}

        <Tooltip title="Cerrar sesión" placement={open ? 'top' : 'right'} arrow>
          <IconButton
            onClick={handleLogout}
            size="small"
            sx={{ color: COLORS.icon, '&:hover': { color: COLORS.textActive } }}
          >
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Drawer>
  );
}

export default DirectorSidebar;
